import { Link } from "react-router-dom";
import { CalendarCheck, Calculator, BadgeCheck, ShieldCheck, UserCheck, Users, Languages, ArrowRight, FileText, Star, MapPin, Phone, Camera } from "lucide-react";
import { CtaButton } from "@/components/site/CtaButton";
import { Eyebrow } from "@/components/site/Sections";
import { HOME_INSPECTIONS, WHY, HOW, TRUST, BIO, CREDENTIALS, MAIN_AREAS, CITY_AREAS, HOME_FAQ, MAP_ALL } from "@/data/homeSite";
import { BRAND } from "@/data/content";

const TRUST_ICONS = [BadgeCheck, ShieldCheck, UserCheck, Users, Languages];
const H2 = ({ children }) => <h2 className="font-display text-3xl font-bold tracking-tight text-[#0B132B] sm:text-4xl">{children}</h2>;
const Wrap = ({ id, className = "", children, testid }) => (
  <section id={id} data-testid={testid} className={`py-20 lg:py-24 ${className}`}><div className="mx-auto max-w-7xl px-4 lg:px-8">{children}</div></section>
);

export const PhotoPlaceholder = ({ label, className = "" }) => (
  <div data-testid="photo-placeholder" className={`flex flex-col items-center justify-center gap-2 rounded-3xl border-2 border-dashed border-gold/50 bg-[#FAF8F1] text-center text-sm text-slate-500 ${className}`}>
    <Camera className="h-8 w-8 text-gold" /> {label}
  </div>
);

export const Hero = () => (
  <section className="relative overflow-hidden border-b border-slate-200 bg-[#F5F3EC]">
    <div className="absolute inset-0 grid-texture opacity-40" />
    <div className="relative mx-auto max-w-7xl px-4 py-16 lg:px-8 lg:py-24">
      <div className="max-w-4xl">
        <Eyebrow>Property Inspections · Pennsylvania</Eyebrow>
        <h1 data-testid="home-hero-title" className="mt-4 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-[#0B132B] sm:text-5xl lg:text-6xl">Pennsylvania property inspections you can act on.</h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">InterNACHI-certified inspections for home buyers, sellers, investors, lenders and property managers — with clear photo reports delivered within 24 hours.</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <CtaButton to="/property-inspections/book" size="lg" data-testid="home-hero-book"><CalendarCheck className="h-4 w-4" /> Book an Inspection</CtaButton>
          <CtaButton to="/request-inspection" variant="slate" size="lg" data-testid="home-hero-quote"><Calculator className="h-4 w-4" /> Get a Quote in Minutes</CtaButton>
        </div>
        <ul className="mt-8 flex flex-wrap gap-x-5 gap-y-2" data-testid="home-trust-bar">
          {TRUST.map((t, i) => { const I = TRUST_ICONS[i]; return <li key={t} className="flex items-center gap-1.5 text-sm font-medium text-[#0B132B]"><I className="h-4 w-4 text-gold" /> {t}</li>; })}
        </ul>
      </div>
    </div>
  </section>
);

export const Services = () => (
  <Wrap testid="home-services">
    <H2>Inspections for every stage of a property.</H2>
    <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {HOME_INSPECTIONS.map((s) => (
        <article key={s.id} data-testid={`home-service-${s.id}`} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-7 transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-22px_rgba(11,19,43,0.35)]">
          <h3 className="font-display text-xl font-bold text-[#0B132B]">{s.name}</h3>
          <p className="mt-2 text-sm font-semibold text-gold">{s.price}</p>
          <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{s.text}</p>
          <Link to={s.href} data-testid={`home-service-cta-${s.id}`} className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#0B132B] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-gold hover:text-[#0B132B]">{s.cta} <ArrowRight className="h-4 w-4" /></Link>
        </article>
      ))}
      <article data-testid="home-service-property-preservation" className="flex flex-col rounded-2xl border border-gold/40 bg-[#FAF8F1] p-7 transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-22px_rgba(11,19,43,0.35)]">
        <h3 className="font-display text-xl font-bold text-[#0B132B]">Property Preservation</h3>
        <p className="mt-2 text-sm font-semibold text-gold">For banks, servicers & asset managers</p>
        <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">Securing, winterization, trash-outs, lawn & snow, evictions and REO services across Central PA, with timestamped before, during and after photos.</p>
        <Link to="/property-preservation" data-testid="home-service-cta-property-preservation" className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#0B132B] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-gold hover:text-[#0B132B]">Explore Preservation Services <ArrowRight className="h-4 w-4" /></Link>
      </article>
    </div>
    <Link to="/inspections" data-testid="home-all-inspections-link" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#0B132B] underline decoration-gold decoration-2 underline-offset-4">See all inspection services and pricing <ArrowRight className="h-4 w-4" /></Link>
  </Wrap>
);

export const Why = () => (
  <Wrap className="bg-[#0B132B] text-white" testid="home-why">
    <h2 className="font-display text-3xl font-bold sm:text-4xl">Why clients choose Vertexhub.</h2>
    <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-5">
      {WHY.map(([t, d]) => <div key={t} className="border-t border-gold/40 pt-5"><h3 className="font-display text-lg font-bold">{t}</h3><p className="mt-2 text-sm leading-relaxed text-slate-300">{d}</p></div>)}
    </div>
  </Wrap>
);

export const How = () => (
  <Wrap testid="home-how">
    <H2>Book in minutes. Get answers in 24 hours.</H2>
    <ol className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
      {HOW.map(([t, d], i) => <li key={t}><span className="font-mono-x text-sm text-gold">0{i + 1}</span><h3 className="mt-2 font-display text-xl font-bold text-[#0B132B]">{t}</h3><p className="mt-2 text-sm text-slate-600">{d}</p></li>)}
    </ol>
  </Wrap>
);

export const SampleReport = () => (
  <Wrap className="bg-[#F5F3EC]" testid="home-sample-report">
    <div className="flex flex-col gap-6 rounded-3xl border border-slate-200 bg-white p-8 sm:flex-row sm:items-center sm:justify-between sm:p-12">
      <div><H2>See what you get before you book.</H2><p className="mt-3 max-w-2xl text-slate-600">Every report includes photos of each finding, a priority summary and plain-language explanations.</p></div>
      {BRAND.sampleReportUrl
        ? <CtaButton href={BRAND.sampleReportUrl} size="lg" data-testid="sample-report-btn"><FileText className="h-4 w-4" /> View a Sample Report (PDF)</CtaButton>
        : <span data-testid="sample-report-btn" className="inline-flex items-center gap-2 rounded-full border border-dashed border-slate-400 px-6 py-3 text-sm text-slate-500"><FileText className="h-4 w-4" /> Sample report (PDF) coming soon</span>}
    </div>
  </Wrap>
);

export const MeetAline = ({ heading = "Meet your inspector.", asH1 = false }) => (
  <Wrap testid="meet-aline">
    <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
      <PhotoPlaceholder label="Professional photo of Kennedy — coming soon" className="min-h-[420px]" />
      <div>
        {asH1 ? <h1 className="font-display text-4xl font-extrabold text-[#0B132B] sm:text-5xl">{heading}</h1> : <H2>{heading}</H2>}
        <p className="mt-4 font-semibold text-gold">Kennedy — Founder & InterNACHI®-Certified Inspector</p>
        {BIO.map((p) => <p key={p.slice(0, 20)} className="mt-4 leading-relaxed text-slate-600">{p}</p>)}
        <ul className="mt-6 grid gap-2 sm:grid-cols-2">{CREDENTIALS.map((c) => <li key={c} className="flex gap-2 text-sm font-medium text-[#0B132B]"><BadgeCheck className="h-4 w-4 text-gold" /> {c}</li>)}</ul>
      </div>
    </div>
  </Wrap>
);

export const Reviews = () => (
  <Wrap className="bg-[#F5F3EC]" testid="home-reviews">
    <H2>What our clients say.</H2>
    <div className="mt-8 rounded-3xl border border-slate-200 bg-white p-8 sm:p-10">
      <div className="flex gap-1 text-gold">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-5 w-5" />)}</div>
      <p className="mt-4 max-w-2xl text-slate-600">We're growing our reviews. Inspected with us? Your feedback helps other Pennsylvania buyers and investors.</p>
      {BRAND.googleReviewUrl
        ? <CtaButton href={BRAND.googleReviewUrl} className="mt-6" data-testid="leave-review-btn">Leave a Google Review</CtaButton>
        : <span data-testid="leave-review-btn" className="mt-6 inline-block rounded-full border border-dashed border-slate-400 px-5 py-2.5 text-sm text-slate-500">Leave a Google Review — link coming soon</span>}
    </div>
  </Wrap>
);

const INVESTOR = [
  ["PA Auction Support", "Pre-auction research, exterior inspections, auction-day logistics and post-sale deed and recording tracking for Pennsylvania sheriff and tax sales. Packages from $2,000.", "See Auction Support", "/packages"],
  ["Section 8 Investment Support", "Help preparing Pennsylvania rental properties for the Housing Choice Voucher program, in English and Portuguese.", "Learn More", "/section8"],
  ["Financing Support", "We connect investors with partner lenders for DSCR, fix & flip, bridge, rental and portfolio loans. We are not a lender; terms and approval are set by the lender.", "Explore Financing", "/financing"],
];
export const Investors = () => (
  <Wrap testid="home-investors">
    <H2>Beyond the inspection: support for Pennsylvania investors.</H2>
    <div className="mt-10 grid gap-5 lg:grid-cols-3">
      {INVESTOR.map(([t, d, c, to]) => <div key={t} className="rounded-2xl border border-slate-200 bg-white p-6"><h3 className="font-display text-lg font-bold text-[#0B132B]">{t}</h3><p className="mt-2 text-sm text-slate-600">{d}</p><Link to={to} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-gold">{c} <ArrowRight className="h-4 w-4" /></Link></div>)}
    </div>
  </Wrap>
);

export const AreasBlock = () => (
  <Wrap className="bg-[#F5F3EC]" testid="home-areas">
    <H2>Two offices. Local coverage across Pennsylvania.</H2>
    <div className="mt-10 grid gap-6 lg:grid-cols-3">
      {MAIN_AREAS.map((a) => (
        <Link key={a.slug} to={`/service-areas/${a.slug}`} data-testid={`area-col-${a.slug}`} className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-gold">
          <h3 className="font-display text-lg font-bold text-[#0B132B]">{a.name}</h3>
          {a.offices.map((o) => <p key={o} className="mt-2 flex gap-2 text-xs text-slate-500"><MapPin className="h-4 w-4 shrink-0 text-gold" /> {o}</p>)}
          <p className="mt-3 text-sm text-slate-600"><b>{a.counties.join(", ")}</b>{a.counties.length > 2 ? " counties" : ""}: {a.towns.join(", ")}.</p>
        </Link>
      ))}
    </div>
    <div className="mt-6 flex flex-wrap items-center gap-2 text-sm" data-testid="city-area-links"><span className="text-slate-500">Local inspection pages:</span>{CITY_AREAS.map((c) => <Link key={c.slug} to={`/service-areas/${c.slug}`} data-testid={`city-link-${c.slug}`} className="rounded-full bg-white px-4 py-1.5 text-[#0B132B] hover:text-gold">{c.name}</Link>)}</div>
    <p className="mt-6 text-sm text-slate-600">Not sure if we cover your address? Call <a href="tel:+12154454257" className="font-semibold text-[#0B132B] underline">(215) 445-4257</a> — we confirm before booking.</p>
    <iframe title="Vertexhub coverage map" src={MAP_ALL} loading="lazy" className="mt-8 h-[380px] w-full rounded-3xl border border-slate-200" data-testid="home-map" />
    <div className="mt-3 flex flex-wrap gap-4 text-xs">{BRAND.offices.map((o) => <a key={o.name} href={o.href} target="_blank" rel="noopener noreferrer" className="text-gold underline">{o.name} on Google Maps</a>)}</div>
  </Wrap>
);

export const FaqBlock = () => (
  <Wrap testid="home-faq">
    <H2>Frequently asked questions</H2>
    <div className="mt-10 grid gap-4 lg:grid-cols-2">
      {HOME_FAQ.map((f) => <details key={f.q} className="rounded-2xl border border-slate-200 bg-white p-5"><summary className="cursor-pointer font-semibold text-[#0B132B]">{f.q}</summary><p className="mt-3 text-sm text-slate-600">{f.a}</p></details>)}
    </div>
  </Wrap>
);

export const CallButton = () => (
  <CtaButton href="tel:+12154454257" variant="slate" size="lg" data-testid="final-call-btn"><Phone className="h-4 w-4" /> Call (215) 445-4257</CtaButton>
);
